/**
 * Performs deep nested comparison between two values to determine if they are equal.
 * Supports comparison of objects, arrays, primitives, and Date objects.
 * @param a any - The first value to compare.
 * @param b any - The second value to compare.
 * @returns boolean - True if the values are equal, false otherwise.
 */
function isEqual(a: any, b: any): boolean {
  if (a === b) return true;

  // Compare types
  const typeA: string = typeof a;
  const typeB: string = typeof b;

  if (typeA !== typeB) return false;

  // Compare arrays
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!isEqual(a[i], b[i])) return false;
    }
    return true;
  }

  // Compare objects
  if (typeA === "object") {
    const keysA: string[] = Object.keys(a);
    const keysB: string[] = Object.keys(b);
    if (keysA.length !== keysB.length) return false;
    for (const key of keysA) {
      if (!isEqual(a[key], b[key])) return false;
    }
    return true;
  }

  // Compare dates
  if (a instanceof Date && b instanceof Date) {
    return a.getTime() === b.getTime();
  }

  // Compare other types
  return false;
}

/**
 * Helper function to check if the @param value is null or undefined, if it's an array it
 * checks for its length.
 *
 * @param value
 * @returns
 */
const isEmpty = (value: any): boolean => {
  // empty values => null undefined "" 0
  if (!value) {
    return true;
  }

  // Empty string
  if (typeof value === "string") {
    return value.trim() === "";
  }

  // Empty Date
  if (value instanceof Date) {
    return value.getTime() <= 0 || isNaN(value.getTime());
  }

  // Empty Map or Set
  if (value instanceof Map || value instanceof Set) {
    return value.size === 0;
  }

  // Empty Error
  if (value instanceof Error) {
    return value.message === "";
  }

  // Object = {}
  if (typeof value === "object" && !Array.isArray(value)) {
    return !Object.keys(value).length;
  }

  // Array = []
  if (Array.isArray(value)) {
    return value.length === 0;
  }

  // For other types, consider them not empty
  return false;
};

/**
 * Creates a debounced version of the provided function, which delays the invocation
 * of the function until after `delay` milliseconds have passed since the last time
 * the debounced function was called.
 *
 * @param func - The function to debounce.
 * @param delay - The number of milliseconds to delay; default is 0.
 * @returns A debounced version of the provided function.
 */
const debounce = (func: (param: any) => void, delay: number = 0) => {
  let timerRef: NodeJS.Timeout | null = null;

  return (param: any) => {
    // If there is an existing timer, clear it to reset the delay
    if (timerRef) {
      clearTimeout(timerRef);
    }
    // Set a new timer that invokes the function after the specified delay
    timerRef = setTimeout(func.bind(this, param), delay);
  };
};

/**
 * Type guard to check if a value is not empty, null, or undefined.
 * Allows TypeScript to narrow the type after checking.
 */
function isNotEmpty<T>(value: T | null | undefined): value is T {
  return !isEmpty(value);
}

export { isEqual, isEmpty, debounce, isNotEmpty };
